import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "digital-advertising-ppc-colorado",
  metaTitle: "Digital Advertising & PPC Management in Colorado",
  metaDescription: "Digital advertising and PPC management for Colorado businesses. Your ad budget goes straight to the platforms — no agency markup. Clear pricing, owner-led.",
  breadcrumbs: [{ label: "Home", to: "/" }],
  currentLabel: "Digital Advertising & PPC Management",
  eyebrow: "Digital Advertising Services — Colorado",
  h1: (
    <>
      Digital Advertising & <span className="text-brand-gold">PPC Management</span> in Colorado
    </>
  ),
  heroDescription: "Paid search and paid social management for Colorado businesses that want every ad dollar tracked to calls and booked jobs.",
  intro: "Looking for digital advertising services or PPC management for small business? We manage Google Ads, Bing, and social ad campaigns for Colorado companies — and your ad budget goes directly to the ad platforms, never through our bank account. No markup, no hidden fees. Management starts at $2,750/month as part of our Expert plan.",
  definition: { term: "PPC management", body: "Running and improving paid ads so every click is worth more than it costs — better keywords, better ads, better landing pages, and no wasted spend." },
  whatWeDo: [
    { title: "Ad audits", body: "We find wasted clicks, bad keywords, and settings that burn budget." },
    { title: "Google & Bing Ads", body: "Search campaigns built around the searches that bring real jobs." },
    { title: "Social ads", body: "Facebook, Instagram, and other platforms where your customers scroll." },
    { title: "Tracking setup", body: "Calls, forms, and bookings tracked so you know what each dollar did." },
    { title: "Zero markup", body: "Ad spend goes directly to the ad platform — you pay them, not us." },
    { title: "No lock-in", body: "Month-to-month. You own your accounts and your data." },
  ],
  whyThisMarket: [
    { heading: "Most agencies add a markup", body: "Many agencies charge a % of your ad spend or pad your budget. We do neither — you pay the platform directly." },
    { heading: "Free leads first", body: "Before spending on ads, we make sure your website, SEO, Google Maps, and AI search are pulling free leads." },
    { heading: "Small budgets need precision", body: "PPC for small business is about tight keyword lists and clean tracking, not big spend." },
  ],
  relatedLinks: [
    { to: "/pricing", label: "Pricing" },
    { to: "/free-audit", label: "Get a free marketing audit" },
    { to: "/free-mockup", label: "Get a free website mockup" },
    { to: "/affordable-seo-small-business-colorado", label: "Affordable SEO for small businesses" },
    { to: "/web-design-castle-rock-colorado", label: "Castle Rock web design company" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/learn", label: "Learn hub" },
  ],
  faqs: [
    { q: "How much does PPC management cost?", a: "PPC management is part of our Expert plan at $2,750/month. Your ad budget is separate and goes directly to the ad platform — we never take a cut of it." },
    { q: "Do you mark up my ad spend?", a: "No. You pay the ad platforms directly. We only charge our flat monthly management fee, and ads can use any platform — Google, Bing, Facebook, and more." },
    { q: "Am I locked into a contract?", a: "No. Everything is month-to-month. You own your ad accounts, your data, and your website." },
    { q: "Should I run ads if my SEO is weak?", a: "Usually not yet. Ads cost more when your site and reviews are thin. We start with free channels first, then layer on paid ads when they will actually convert." },
  ],
  serviceName: "Digital Advertising & PPC Management in Colorado",
  serviceDescription: "Digital advertising and PPC management for Colorado businesses. Your ad budget goes straight to the platforms — no agency markup. Clear pricing, owner-led.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/digital-advertising-ppc-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
